import React from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  useDeleteFacilityRequest,
  FacilityRequest,
  FacilityRequestForm,
  FacilityRequestFormField,
  FacilityRequestFormFieldType,
  FacilityRequestStatus,
  useCreateFacilityBlackoutDate,
  useDeleteFacilityBlackoutDate,
  useFacilityBlackoutDates,
  useFacilityRequestForm,
  useFacilityRequests,
  useSaveFacilityRequestForm,
  useFacilitySettings,
  useSyncFacilityCalendar,
  useUpdateFacilityRequest,
  useUpdateFacilitySettings,
} from '../hooks/useFacilityRequests';
import { useGroups } from '../hooks/useGroups';
import { supabase } from '../services/supabase';

const PageOuter = styled.div`
  min-height: 100%;
  padding: clamp(1rem, 3vw, 2rem);
  background: linear-gradient(
    165deg,
    color-mix(in srgb, var(--primary) 6%, transparent) 0%,
    transparent 42%,
    color-mix(in srgb, var(--surface) 100%, transparent) 100%
  );
`;

const Page = styled.div`
  display: grid;
  gap: 1.25rem;
  max-width: 1120px;
  margin: 0 auto;
`;

const Card = styled.section`
  border: 1px solid color-mix(in srgb, var(--outline) 85%, transparent);
  border-radius: 18px;
  padding: clamp(1rem, 2.5vw, 1.35rem);
  background: var(--surface);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 12px 40px -18px rgba(0, 0, 0, 0.12);
`;

const HeroCard = styled(Card)`
  position: relative;
  /* Do not clip overflow: the settings dropdown is absolutely positioned and must paint outside this card. */

  &::before {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: 4px;
    background: linear-gradient(180deg, var(--primary), color-mix(in srgb, var(--primary) 45%, var(--surface)));
    border-radius: 18px 0 0 18px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(1.35rem, 2.5vw, 1.65rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--onSurface);
`;

const SubTitle = styled.h2`
  margin: 0 0 0.85rem 0;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--onSurfaceVariant);
`;

const Muted = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
  line-height: 1.5;
`;

const FeedbackBanner = styled.div`
  margin-top: 0.75rem;
  padding: 0.65rem 0.85rem;
  border-radius: 12px;
  font-size: 0.88rem;
  color: var(--onSurface);
  background: color-mix(in srgb, var(--primary) 10%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--primary) 22%, transparent);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
`;

const Input = styled.input`
  border: 1px solid color-mix(in srgb, var(--outline) 90%, transparent);
  border-radius: 12px;
  padding: 0.6rem 0.75rem;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--primary) 55%, var(--outline));
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
  }
`;

const TextArea = styled.textarea`
  border: 1px solid color-mix(in srgb, var(--outline) 90%, transparent);
  border-radius: 12px;
  padding: 0.6rem 0.75rem;
  min-height: 88px;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  resize: vertical;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--primary) 55%, var(--outline));
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
  }
`;

const Select = styled.select`
  border: 1px solid color-mix(in srgb, var(--outline) 90%, transparent);
  border-radius: 12px;
  padding: 0.6rem 0.75rem;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    outline: none;
    border-color: color-mix(in srgb, var(--primary) 55%, var(--outline));
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
  }
`;

const Button = styled.button`
  border: 1px solid transparent;
  border-radius: 12px;
  padding: 0.6rem 1rem;
  background: var(--primary);
  color: var(--onPrimary);
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  width: fit-content;
  transition: transform 0.12s ease, filter 0.12s ease, box-shadow 0.12s ease;
  box-shadow: 0 2px 8px color-mix(in srgb, var(--primary) 35%, transparent);

  &:hover:not(:disabled) {
    filter: brightness(1.05);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const SecondaryButton = styled(Button)`
  background: var(--surfaceVariant);
  color: var(--onSurface);
  border-color: color-mix(in srgb, var(--outline) 80%, transparent);
  box-shadow: none;

  &:hover:not(:disabled) {
    background: color-mix(in srgb, var(--onSurface) 6%, var(--surfaceVariant));
  }
`;

const Row = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
`;

const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
`;

const TabButton = styled.button<{ $active?: boolean }>`
  border: 1px solid
    ${({ $active }) =>
      $active ? 'color-mix(in srgb, var(--primary) 45%, var(--outline))' : 'color-mix(in srgb, var(--outline) 80%, transparent)'};
  border-radius: 999px;
  padding: 0.45rem 0.85rem;
  background: ${({ $active }) =>
    $active ? 'color-mix(in srgb, var(--primary) 12%, var(--surface))' : 'color-mix(in srgb, var(--surface) 85%, var(--surfaceVariant))'};
  color: ${({ $active }) => ($active ? 'var(--onSurface)' : 'var(--onSurfaceVariant)')};
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
`;

const HeroHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const HeroHeading = styled.div`
  display: grid;
  gap: 0.4rem;
`;

const MenuWrap = styled.div`
  position: relative;
  z-index: 20;
`;

const MenuButton = styled.button`
  border: 1px solid color-mix(in srgb, var(--outline) 72%, transparent);
  border-radius: 12px;
  padding: 0.55rem 0.8rem;
  background: color-mix(in srgb, var(--surface) 84%, var(--surfaceVariant));
  color: var(--onSurface);
  font-weight: 600;
  cursor: pointer;
`;

const MenuPanel = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 0.45rem);
  z-index: 30;
  width: min(640px, 90vw);
  border: 1px solid color-mix(in srgb, var(--outline) 70%, transparent);
  border-radius: 14px;
  padding: 0.85rem;
  background: var(--surface);
  box-shadow: 0 14px 36px -18px rgba(0, 0, 0, 0.35);
  display: grid;
  gap: 0.85rem;
`;

const RequestList = styled.div`
  display: grid;
  gap: 1.15rem;
`;

const RequestCard = styled.article<{ $status: FacilityRequestStatus; $highlighted?: boolean }>`
  position: relative;
  isolation: isolate;
  border-radius: 22px;
  overflow: hidden;
  padding: 1.2rem 1.25rem 1.25rem;
  display: grid;
  gap: 1rem;
  border: 1px solid color-mix(in srgb, var(--outline) 58%, transparent);
  background: linear-gradient(
    155deg,
    color-mix(in srgb, var(--surface) 92%, var(--primary) 8%) 0%,
    var(--surface) 42%,
    color-mix(in srgb, var(--surfaceVariant) 28%, var(--surface)) 100%
  );
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--onSurface) 4.5%, transparent) inset,
    0 1px 2px rgba(0, 0, 0, 0.04),
    0 18px 52px -28px rgba(0, 0, 0, 0.2);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;

  ${({ $highlighted }) =>
    $highlighted
      ? `
    border-color: color-mix(in srgb, var(--primary) 45%, transparent);
    box-shadow:
      0 0 0 1px color-mix(in srgb, var(--primary) 22%, transparent) inset,
      0 0 0 3px color-mix(in srgb, var(--primary) 24%, transparent),
      0 18px 52px -28px rgba(0, 0, 0, 0.2);
  `
      : ''}

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    z-index: 1;
    background: ${({ $status }) =>
      $status === 'approved'
        ? 'linear-gradient(90deg, rgb(22, 163, 74), rgb(21, 128, 61), color-mix(in srgb, rgb(34, 197, 94) 68%, var(--surfaceVariant)))'
        : $status === 'pending'
          ? 'linear-gradient(90deg, rgb(245, 158, 11), rgb(217, 119, 6), color-mix(in srgb, rgb(251, 191, 36) 65%, var(--surfaceVariant)))'
          : $status === 'declined' || $status === 'cancelled'
            ? 'linear-gradient(90deg, rgb(239, 68, 68), rgb(185, 28, 28), color-mix(in srgb, rgb(248, 113, 113) 62%, var(--surfaceVariant)))'
            : 'linear-gradient(90deg, rgb(59, 130, 246), rgb(29, 78, 216), color-mix(in srgb, rgb(96, 165, 250) 62%, var(--surfaceVariant)))'};
  }

  &:hover {
    transform: translateY(-3px);
    border-color: color-mix(in srgb, var(--primary) 26%, var(--outline));
    box-shadow:
      0 0 0 1px color-mix(in srgb, var(--onSurface) 6%, transparent) inset,
      0 4px 14px rgba(0, 0, 0, 0.06),
      0 26px 64px -24px color-mix(in srgb, var(--primary) 22%, rgba(0, 0, 0, 0.35));
  }

  @media (prefers-reduced-motion: reduce) {
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    &:hover {
      transform: none;
    }
  }
`;

const RequestCardHeader = styled.div`
  display: grid;
  gap: 0;
  padding-top: 0.2rem;
`;

const RequestCardHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.6rem;
  align-items: center;
`;

const CollapseButton = styled.button`
  border: 1px solid color-mix(in srgb, var(--outline) 70%, transparent);
  border-radius: 999px;
  padding: 0.35rem 0.7rem;
  background: color-mix(in srgb, var(--surface) 80%, var(--surfaceVariant));
  color: var(--onSurfaceVariant);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
`;

const RequestCardTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
`;

const RequestHeading = styled.div`
  display: grid;
  gap: 0.4rem;
  min-width: 0;
`;

const RequestTitle = styled.strong`
  font-size: 1.08rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.25;
  color: var(--onSurface);
`;

const RequestTitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem 0.6rem;
  min-width: 0;
`;

const SourceChip = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--outline) 55%, transparent);
  background: color-mix(in srgb, var(--surface) 65%, var(--surfaceVariant));
  color: var(--onSurfaceVariant);
  width: fit-content;
  box-shadow: 0 1px 0 color-mix(in srgb, var(--onSurface) 5%, transparent) inset;

  @supports (backdrop-filter: blur(8px)) {
    backdrop-filter: blur(10px);
    background: color-mix(in srgb, var(--surface) 55%, transparent);
  }
`;

const RequestMeta = styled.div`
  display: grid;
  gap: 0.28rem;
  font-size: 0.84rem;
  line-height: 1.45;
`;

const RequestMetaPrimary = styled.span`
  color: color-mix(in srgb, var(--onSurface) 88%, var(--onSurfaceVariant));
  font-weight: 500;
`;

const RequestMetaSecondary = styled.span`
  font-size: 0.8rem;
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  color: var(--onSurfaceVariant);
  opacity: 0.95;
`;

const StatusPill = styled.span<{ $status: FacilityRequestStatus }>`
  font-size: 0.79rem;
  font-weight: 700;
  padding: 0.4rem 0.86rem;
  border-radius: 999px;
  border: 1px solid transparent;
  white-space: nowrap;
  letter-spacing: 0.02em;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--surface) 65%, transparent), 0 1px 2px rgba(0, 0, 0, 0.08);

  ${({ $status }) =>
    $status === 'approved'
      ? `background: color-mix(in srgb, rgb(22, 163, 74) 14%, var(--surface)); color: rgb(21, 128, 61); border-color: color-mix(in srgb, rgb(22, 163, 74) 28%, transparent);`
      : $status === 'pending'
        ? `background: color-mix(in srgb, rgb(245, 158, 11) 16%, var(--surface)); color: rgb(180, 83, 9); border-color: color-mix(in srgb, rgb(245, 158, 11) 30%, transparent);`
        : $status === 'declined'
          ? `background: color-mix(in srgb, rgb(239, 68, 68) 12%, var(--surface)); color: rgb(185, 28, 28); border-color: color-mix(in srgb, rgb(239, 68, 68) 25%, transparent);`
          : $status === 'needs_info'
            ? `background: color-mix(in srgb, rgb(59, 130, 246) 14%, var(--surface)); color: rgb(29, 78, 216); border-color: color-mix(in srgb, rgb(59, 130, 246) 28%, transparent);`
            : `background: color-mix(in srgb, var(--onSurface) 10%, var(--surface)); color: var(--onSurfaceVariant); border-color: var(--outline);`}
`;

const RequestDetailsPanel = styled.div`
  border-radius: 16px;
  padding: 0.95rem 1rem 1rem;
  background: color-mix(in srgb, var(--surfaceVariant) 38%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--outline) 48%, transparent);
  box-shadow:
    inset 0 1px 0 color-mix(in srgb, var(--onSurface) 7%, transparent),
    0 1px 0 color-mix(in srgb, var(--onSurface) 4%, transparent);
`;

const RequestDetails = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(164px, 1fr));
  gap: 0.55rem;
  margin: 0;
  padding: 0;
`;

const RequestDetail = styled.div`
  display: grid;
  gap: 0.3rem;
  padding: 0.55rem 0.68rem;
  border-radius: 12px;
  background: color-mix(in srgb, var(--surface) 82%, transparent);
  border: 1px solid color-mix(in srgb, var(--outline) 42%, transparent);
  box-shadow: 0 1px 0 color-mix(in srgb, var(--onSurface) 4%, transparent) inset;
  min-width: 0;

  dt {
    color: var(--onSurfaceVariant);
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.055em;
  }

  dd {
    margin: 0;
    color: var(--onSurface);
    font-size: 0.875rem;
    white-space: pre-wrap;
    line-height: 1.48;
  }
`;

const RequestCardFooter = styled.div`
  display: grid;
  gap: 0.9rem;
`;

const RequestStatusField = styled(Field)`
  & > select {
    width: 100%;
  }
`;

const AdminNotesCollapsible = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const NotesToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  padding: 0.62rem 0.8rem;
  border-radius: 14px;
  border: 1px solid color-mix(in srgb, var(--outline) 50%, transparent);
  background: color-mix(in srgb, var(--surface) 75%, var(--surfaceVariant));
  color: var(--onSurface);
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 1px 0 color-mix(in srgb, var(--onSurface) 4%, transparent) inset;

  &:hover {
    background: color-mix(in srgb, var(--surface) 60%, var(--surfaceVariant));
    border-color: color-mix(in srgb, var(--outline) 75%, transparent);
    box-shadow:
      0 1px 0 color-mix(in srgb, var(--onSurface) 5%, transparent) inset,
      0 0 0 1px color-mix(in srgb, var(--primary) 12%, transparent);
  }
`;

const NotesToggleLeft = styled.span`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
`;

const NotesPreview = styled.span`
  font-size: 0.78rem;
  font-weight: 400;
  color: var(--onSurfaceVariant);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NotesChevron = styled.span<{ $open: boolean }>`
  flex-shrink: 0;
  font-size: 1.1rem;
  line-height: 1;
  color: var(--onSurfaceVariant);
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  transition: transform 0.2s ease;
`;

const RequestActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  padding-top: 0.25rem;
`;

const DangerButton = styled(SecondaryButton)`
  border-color: color-mix(in srgb, rgb(239, 68, 68) 45%, transparent);
  color: rgb(185, 28, 28);

  &:hover:not(:disabled) {
    background: color-mix(in srgb, rgb(239, 68, 68) 10%, var(--surfaceVariant));
  }
`;

const BlackoutList = styled.div`
  display: grid;
  gap: 0.55rem;
`;

const BlackoutItem = styled.div`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.65rem;
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: center;
`;

const ChoiceGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ChoiceOption = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.75rem;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--outline) 78%, transparent);
  background: color-mix(in srgb, var(--surface) 78%, var(--surfaceVariant));
  color: var(--onSurface);
  cursor: pointer;
  font-size: 0.84rem;
`;

const CalendarShell = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const CalendarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const CalendarMonthLabel = styled.strong`
  font-size: 1rem;
`;

const CalendarNavButton = styled.button`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.4rem 0.65rem;
  background: var(--surfaceVariant);
  color: var(--onSurface);
  cursor: pointer;
`;

const CalendarWeekdays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.4rem;
`;

const CalendarWeekday = styled.div`
  font-size: 0.72rem;
  color: var(--onSurfaceVariant);
  text-align: center;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.4rem;
`;

const CalendarDayCell = styled.button<{
  $selected?: boolean;
  $hasBlackout?: boolean;
  $hasBooked?: boolean;
  $hasPending?: boolean;
}>`
  border: 1px solid
    ${({ $hasBlackout, $hasBooked, $selected }) =>
      $selected
        ? 'var(--primary)'
        : $hasBlackout
          ? 'rgba(239, 68, 68, 0.45)'
          : $hasBooked
            ? 'rgba(22, 163, 74, 0.4)'
            : 'var(--outline)'};
  border-radius: 10px;
  min-height: 72px;
  padding: 0.4rem;
  background: ${({ $selected, $hasBlackout, $hasBooked, $hasPending }) =>
    $selected
      ? 'rgba(59, 130, 246, 0.12)'
      : $hasBlackout
        ? 'rgba(239, 68, 68, 0.14)'
        : $hasBooked
          ? 'rgba(22, 163, 74, 0.12)'
          : $hasPending
            ? 'rgba(245, 158, 11, 0.12)'
            : 'var(--surfaceVariant)'};
  color: var(--onSurface);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  cursor: pointer;

  @media (max-width: 700px) {
    min-height: 58px;
    padding: 0.3rem;
  }
`;

const CalendarDayNumber = styled.span`
  font-weight: 600;
  font-size: 0.85rem;
`;

const CalendarLegend = styled.div`
  display: flex;
  gap: 0.9rem;
  flex-wrap: wrap;
  font-size: 0.8rem;
  color: var(--onSurfaceVariant);
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`;

const LegendDot = styled.i<{ $color: string }>`
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  display: inline-block;
`;

const DaySummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const DayPill = styled.span<{ $tone: 'booking' | 'blackout' | 'pending' }>`
  font-size: 0.65rem;
  border-radius: 999px;
  padding: 0.12rem 0.4rem;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'blackout' ? 'rgba(239, 68, 68, 0.55)' : $tone === 'pending' ? 'rgba(245, 158, 11, 0.55)' : 'rgba(34, 197, 94, 0.6)'};
  color: ${({ $tone }) =>
    $tone === 'blackout' ? 'rgb(239, 68, 68)' : $tone === 'pending' ? 'rgb(245, 158, 11)' : 'rgb(22, 163, 74)'};
`;

const SelectedDayPanel = styled.div`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.7rem;
  background: var(--surfaceVariant);
  display: grid;
  gap: 0.45rem;
`;

const SelectedDayItem = styled.div`
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.5rem;
  background: var(--surface);
`;

type RequestDraft = {
  status: FacilityRequestStatus;
  adminNotes: string;
  adminResponse: string;
};

/** Read-only display: e.g. May 2, 2026, 3:45 PM (12-hour, US month/day/year). */
const DISPLAY_DATETIME: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

const formatDisplayDateTime = (iso: string) => {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('en-US', DISPLAY_DATETIME);
};

const fromLocalInputValue = (value: string) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

const formatOptional = (value?: string | number | null) => {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '—';
  return value?.toString().trim() || '—';
};

const formatList = (values?: Array<string | null | undefined> | null) => {
  const filtered =
    values?.map((value) => (typeof value === 'string' ? value.trim() : '')).filter(Boolean) ?? [];
  return filtered.length > 0 ? filtered.join(', ') : '—';
};

const formatLocationSummary = (
  values?: Array<string | null | undefined> | null,
  locationOther?: string | null,
) => {
  const filtered =
    values?.map((value) => (typeof value === 'string' ? value.trim() : '')).filter(Boolean) ?? [];
  const otherText = locationOther?.trim() || '';

  if (filtered.length === 0) {
    return otherText || 'Unspecified location';
  }

  if (filtered.includes('Other') && otherText) {
    return `${filtered.join(', ')} (${otherText})`;
  }

  return filtered.join(', ');
};

const formatBlackoutScope = (
  values?: Array<string | null | undefined> | null,
  locationOther?: string | null,
) => {
  const filtered =
    values?.map((value) => (typeof value === 'string' ? value.trim() : '')).filter(Boolean) ?? [];
  const otherText = locationOther?.trim() || '';

  if (filtered.length === 0) {
    return 'Campus-wide';
  }

  if (filtered.includes('Other') && otherText) {
    return `${filtered.join(', ')} (${otherText})`;
  }

  return filtered.join(', ');
};

const formatCountLabel = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

const formatYesNo = (value?: boolean | null) => (value ? 'Yes' : 'No');

const formatCustomAnswer = (value: unknown) => {
  if (Array.isArray(value)) {
    const normalized = value.map((item) => String(item || '').trim()).filter(Boolean);
    return normalized.length > 0 ? normalized.join(', ') : '—';
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '—';
  if (value && typeof value === 'object') return JSON.stringify(value);
  return String(value || '').trim() || '—';
};

const STATUS_OPTIONS: FacilityRequestStatus[] = ['pending', 'approved', 'declined', 'needs_info', 'cancelled'];

const STATUS_DISPLAY: Record<FacilityRequestStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  declined: 'Declined',
  needs_info: 'Needs info',
  cancelled: 'Cancelled',
};

const STATUS_BADGE_TEXT: Record<FacilityRequestStatus, string> = {
  pending: 'Pending review',
  approved: 'Approved',
  declined: 'Declined',
  needs_info: 'Needs info',
  cancelled: 'Cancelled',
};

const FACILITY_LOCATION_OPTIONS = ['Fellowship Hall', 'Gym', 'Sanctuary', 'Other'];

const truncateOneLine = (text: string, max = 72) => {
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
};
const CALENDAR_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const FORM_FIELD_TYPE_OPTIONS: Array<{ value: FacilityRequestFormFieldType; label: string }> = [
  { value: 'short_text', label: 'Short text' },
  { value: 'long_text', label: 'Long text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Select' },
  { value: 'multi_select', label: 'Multi-select' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date' },
];

const FORM_FIELD_TYPES_REQUIRING_OPTIONS: FacilityRequestFormFieldType[] = ['select', 'multi_select', 'checkbox'];
const FLOOR_LAYOUT_CORE_KEY = 'floor_layout';
const FLOOR_LAYOUT_IMAGE_BUCKET = 'post-images';

const CORE_BOOKING_FIELDS: Array<{
  key: string;
  label: string;
  type: FacilityRequestFormFieldType;
  required: boolean;
  options?: string[];
}> = [
  { key: 'requester_name', label: 'Full Name', type: 'short_text', required: true },
  { key: 'requester_email', label: 'Email', type: 'email', required: true },
  { key: 'requester_phone', label: 'Phone', type: 'phone', required: false },
  { key: 'organization_name', label: 'Organization', type: 'short_text', required: false },
  { key: 'requested_start_at', label: 'Requested Start', type: 'date', required: true },
  { key: 'requested_end_at', label: 'Requested End', type: 'date', required: true },
  { key: 'event_name', label: 'Event Name', type: 'short_text', required: false },
  {
    key: 'ministry_group',
    label: 'Ministry / Group',
    type: 'select',
    required: true,
    options: ['Hospitality', 'Children', 'Worship', 'Outreach', 'Technical', 'Prayer', 'Other'],
  },
  { key: 'ministry_group_other', label: 'Specify Ministry / Group', type: 'short_text', required: false },
  { key: 'event_type', label: 'Event Type', type: 'select', required: true, options: ['Private', 'Ministry', 'Other'] },
  { key: 'event_type_other', label: 'Specify Event Type', type: 'short_text', required: false },
  { key: 'attendee_count', label: 'Estimated Attendees', type: 'number', required: false },
  {
    key: 'location_of_event',
    label: 'Location of Event',
    type: 'multi_select',
    required: true,
    options: FACILITY_LOCATION_OPTIONS,
  },
  { key: 'location_other', label: 'Other Event Location', type: 'short_text', required: false },
  { key: 'description_of_event', label: 'Description of Event', type: 'long_text', required: true },
  { key: 'table_types', label: 'Types of Tables', type: 'multi_select', required: true, options: ['Rectangle', 'Round', 'Mix', 'None'] },
  { key: 'number_of_tables_needed', label: 'Number of Tables Needed', type: 'number', required: false },
  { key: 'food_table_count', label: 'Food-serving tables', type: 'number', required: false },
  { key: 'needs_table_linen', label: 'Needs table linen?', type: 'checkbox', required: false },
  { key: 'has_audio_visual_needs', label: 'Has audio/visual needs?', type: 'checkbox', required: false },
  { key: 'audio_visual_needs_details', label: 'Audio/visual needs details', type: 'long_text', required: false },
  { key: 'floor_layout', label: 'Floor layout', type: 'select', required: true },
  { key: 'is_recurring_event', label: 'Is recurring event?', type: 'checkbox', required: false },
  {
    key: 'recurrence_frequency',
    label: 'Recurrence frequency',
    type: 'select',
    required: false,
    options: ['Daily', 'Weekly', 'Biweekly', 'Monthly', 'Quarterly', 'Annually'],
  },
  { key: 'recurrence_frequency_other', label: 'Specify recurrence frequency', type: 'short_text', required: false },
  { key: 'recurrence_ends_at', label: 'Recurs until', type: 'date', required: false },
];

const coreFieldStorageKey = (key: string) => `core__${key}`;

const readValidationRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const isCoreFormField = (field: FacilityRequestFormField) => {
  const validation = readValidationRecord(field.validation);
  return Boolean(validation.is_core);
};

const buildCoreFieldDraft = (
  coreField: (typeof CORE_BOOKING_FIELDS)[number],
  existingField: FacilityRequestFormField | null,
  sortOrder: number
): FacilityRequestFormField => ({
  ...(existingField || {}),
  field_key: coreFieldStorageKey(coreField.key),
  label: existingField?.label || coreField.label,
  field_type: existingField?.field_type || coreField.type,
  is_required: existingField?.is_required ?? coreField.required,
  sort_order: sortOrder,
  options: Array.isArray(existingField?.options) ? existingField?.options : coreField.options || [],
  validation: {
    ...readValidationRecord(existingField?.validation),
    is_core: true,
    core_key: coreField.key,
  },
  visibility_rules: existingField?.visibility_rules ?? {},
  placeholder: existingField?.placeholder ?? null,
  help_text: existingField?.help_text ?? null,
});

const ensureCoreFields = (fields: FacilityRequestFormField[]) => {
  const coreByKey = new Map<string, FacilityRequestFormField>();
  const customFields: FacilityRequestFormField[] = [];

  fields.forEach((field) => {
    const validation = readValidationRecord(field.validation);
    const validationCoreKey = String(validation.core_key || '').trim();
    const prefixedCoreKey = field.field_key.startsWith('core__') ? field.field_key.slice(6) : '';
    const resolvedCoreKey = validationCoreKey || prefixedCoreKey;
    if (resolvedCoreKey && CORE_BOOKING_FIELDS.some((coreField) => coreField.key === resolvedCoreKey)) {
      coreByKey.set(resolvedCoreKey, field);
      return;
    }
    customFields.push(field);
  });

  const normalizedCoreFields = CORE_BOOKING_FIELDS.map((coreField, index) =>
    buildCoreFieldDraft(coreField, coreByKey.get(coreField.key) || null, index)
  );

  return [...normalizedCoreFields, ...customFields].map((field, index) => ({
    ...field,
    sort_order: index,
  }));
};

const slugifyFieldKey = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const parseOptionsText = (value: string) =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const optionsToMultilineText = (options?: unknown) => {
  if (!Array.isArray(options)) return '';
  return options.map((option) => String(option)).join('\n');
};

type FloorLayoutOption = {
  id: string;
  label: string;
  imageSrc: string;
};

const normalizeFloorLayoutOptions = (options?: unknown): FloorLayoutOption[] => {
  if (!Array.isArray(options)) return [];
  return options
    .map((option, index) => {
      if (!option || typeof option !== 'object' || Array.isArray(option)) return null;
      const record = option as Record<string, unknown>;
      const imageSrc = String(record.imageSrc || record.image_url || record.url || '').trim();
      if (!imageSrc) return null;
      const label = String(record.label || record.value || `Layout ${index + 1}`).trim() || `Layout ${index + 1}`;
      const id = String(record.id || slugifyFieldKey(label) || `layout_${index + 1}`).trim() || `layout_${index + 1}`;
      return { id, label, imageSrc };
    })
    .filter((option): option is FloorLayoutOption => Boolean(option));
};

const fieldTypeLabel = (value: FacilityRequestFormFieldType) =>
  FORM_FIELD_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value;

const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const formatDayKey = (date: Date) =>
  `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(2, '0')}`;

const eachDayInRange = (start: Date, end: Date) => {
  const lastMoment = new Date(end.getTime() - 1);
  if (lastMoment < start) return [] as Date[];
  const days: Date[] = [];
  const cursor = startOfDay(start);
  const endDay = startOfDay(lastMoment);
  while (cursor <= endDay) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
};

type CalendarEntry = {
  id: string;
  tone: 'booking' | 'blackout' | 'pending';
  title: string;
  subtitle: string;
  locationSummary?: string;
};

type FacilityAdminTab = 'requests' | 'calendar' | 'form_builder';

const FacilityAdminPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: requests = [], isLoading: loadingRequests } = useFacilityRequests();
  const { data: blackoutDates = [] } = useFacilityBlackoutDates();
  const { data: settings } = useFacilitySettings();
  const { data: groups = [], isLoading: groupsLoading } = useGroups();
  const { data: facilityRequestForm } = useFacilityRequestForm();
  const updateRequestMutation = useUpdateFacilityRequest();
  const deleteRequestMutation = useDeleteFacilityRequest();
  const createBlackoutMutation = useCreateFacilityBlackoutDate();
  const deleteBlackoutMutation = useDeleteFacilityBlackoutDate();
  const updateSettingsMutation = useUpdateFacilitySettings();
  const syncCalendarMutation = useSyncFacilityCalendar();
  const saveFacilityRequestFormMutation = useSaveFacilityRequestForm();

  const [requestDrafts, setRequestDrafts] = React.useState<Record<string, RequestDraft>>({});
  const [adminNotesOpen, setAdminNotesOpen] = React.useState<Record<string, boolean>>({});
  const [requestExpanded, setRequestExpanded] = React.useState<Record<string, boolean>>({});
  const [settingsMenuOpen, setSettingsMenuOpen] = React.useState(false);
  const [blackoutDraft, setBlackoutDraft] = React.useState({
    title: 'Facility Blackout',
    starts_at: '',
    ends_at: '',
    reason: '',
    location_of_event: [] as string[],
    location_other: '',
    all_day: false,
  });
  const [settingsDraft, setSettingsDraft] = React.useState({
    facilities_group_id: '',
    google_calendar_id: '',
    facilities_group_calendar_id: '',
  });
  const [feedback, setFeedback] = React.useState<string | null>(null);
  const [calendarMonth, setCalendarMonth] = React.useState(() => startOfMonth(new Date()));
  const [selectedDayKey, setSelectedDayKey] = React.useState(() => formatDayKey(new Date()));
  const [activeTab, setActiveTab] = React.useState<FacilityAdminTab>('requests');
  const [floorLayoutUploadByIndex, setFloorLayoutUploadByIndex] = React.useState<Record<number, boolean>>({});
  const [formDraft, setFormDraft] = React.useState<FacilityRequestForm>({
    form_key: 'facility_booking',
    title: 'Facility Booking Additional Questions',
    description: '',
    is_enabled: false,
    fields: ensureCoreFields([]),
  });
  const settingsMenuRef = React.useRef<HTMLDivElement | null>(null);
  const focusedRequestId = searchParams.get('requestId')?.trim() ?? '';
  const groupOptions = React.useMemo(() => {
    const sorted = [...groups].sort((left, right) => left.name.localeCompare(right.name));
    const selectedGroupId = settingsDraft.facilities_group_id.trim();
    if (!selectedGroupId || sorted.some((group) => group.id === selectedGroupId)) {
      return sorted;
    }

    return [
      ...sorted,
      {
        id: selectedGroupId,
        name: `Unknown group (${selectedGroupId})`,
        category: '',
      },
    ];
  }, [groups, settingsDraft.facilities_group_id]);

  React.useEffect(() => {
    const searchTab = searchParams.get('tab');
    if (searchTab === 'requests' || searchTab === 'calendar' || searchTab === 'form_builder') {
      setActiveTab(searchTab);
    }
  }, [searchParams]);

  React.useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', activeTab);
    const nextSerialized = nextParams.toString();
    const currentSerialized = searchParams.toString();
    if (nextSerialized !== currentSerialized) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  React.useEffect(() => {
    if (!focusedRequestId) return;
    setActiveTab('requests');
    setRequestExpanded((prev) => ({ ...prev, [focusedRequestId]: true }));
  }, [focusedRequestId]);

  React.useEffect(() => {
    if (!focusedRequestId || activeTab !== 'requests') return;
    const element = document.getElementById(`facility-request-${focusedRequestId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [activeTab, focusedRequestId, requests]);

  React.useEffect(() => {
    setSettingsDraft({
      facilities_group_id: settings?.facilities_group_id || '',
      google_calendar_id: settings?.google_calendar_id || '',
      facilities_group_calendar_id: settings?.facilities_group_calendar_id || '',
    });
  }, [settings]);

  React.useEffect(() => {
    if (!settingsMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (settingsMenuRef.current && target && !settingsMenuRef.current.contains(target)) {
        setSettingsMenuOpen(false);
      }
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [settingsMenuOpen]);

  React.useEffect(() => {
    if (!facilityRequestForm) return;
    setFormDraft({
      form_key: facilityRequestForm.form_key || 'facility_booking',
      title: facilityRequestForm.title || 'Facility Booking Additional Questions',
      description: facilityRequestForm.description || '',
      is_enabled: Boolean(facilityRequestForm.is_enabled),
      fields: ensureCoreFields(
        (facilityRequestForm.fields || []).map((field, index) => ({
          ...field,
          sort_order: Number.isFinite(field.sort_order) ? field.sort_order : index,
          options: Array.isArray(field.options) ? field.options : [],
        }))
      ),
    });
  }, [facilityRequestForm]);

  const indexedFormFields = React.useMemo(
    () => formDraft.fields.map((field, index) => ({ field, index })),
    [formDraft.fields]
  );
  const coreFieldEntries = React.useMemo(
    () => indexedFormFields.filter(({ field }) => isCoreFormField(field)),
    [indexedFormFields]
  );
  const customFieldEntries = React.useMemo(
    () => indexedFormFields.filter(({ field }) => !isCoreFormField(field)),
    [indexedFormFields]
  );

  const getDraftForRequest = (request: FacilityRequest): RequestDraft =>
    requestDrafts[request.id] || {
      status: request.status,
      adminNotes: request.admin_notes || '',
      adminResponse: request.admin_response || '',
    };

  const onRequestDraftChange = (request: FacilityRequest, next: Partial<RequestDraft>) => {
    setRequestDrafts((prev) => {
      const current = prev[request.id];
      const baseDraft: RequestDraft = {
        status: current?.status ?? request.status,
        adminNotes: current?.adminNotes ?? request.admin_notes ?? '',
        adminResponse: current?.adminResponse ?? request.admin_response ?? '',
      };
      return {
        ...prev,
        [request.id]: { ...baseDraft, ...next },
      };
    });
  };

  const saveRequest = async (request: FacilityRequest) => {
    const draft = getDraftForRequest(request);
    try {
      await updateRequestMutation.mutateAsync({
        id: request.id,
        status: draft.status,
        adminNotes: draft.adminNotes,
        adminResponse: draft.adminResponse,
      });
      setFeedback(`Request ${request.id} updated.`);
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Could not update request.');
    }
  };

  const saveSettings = async () => {
    try {
      await updateSettingsMutation.mutateAsync({
        facilities_group_id: settingsDraft.facilities_group_id || null,
        google_calendar_id: settingsDraft.google_calendar_id || null,
        facilities_group_calendar_id: settingsDraft.facilities_group_calendar_id || null,
      });
      setFeedback('Facilities settings saved.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Could not save settings.');
    }
  };

  const updateFormField = (index: number, next: Partial<FacilityRequestFormField>) => {
    setFormDraft((prev) => {
      const nextFields = prev.fields.map((field, fieldIndex) =>
        fieldIndex === index
          ? {
              ...field,
              ...next,
            }
          : field
      );
      return {
        ...prev,
        fields: nextFields,
      };
    });
  };

  const updateFloorLayoutOption = (fieldIndex: number, optionId: string, next: Partial<FloorLayoutOption>) => {
    setFormDraft((prev) => {
      const nextFields = prev.fields.map((field, index) => {
        if (index !== fieldIndex) return field;
        const options = normalizeFloorLayoutOptions(field.options).map((option) =>
          option.id === optionId ? { ...option, ...next } : option
        );
        return { ...field, options };
      });
      return { ...prev, fields: nextFields };
    });
  };

  const removeFloorLayoutOption = (fieldIndex: number, optionId: string) => {
    setFormDraft((prev) => {
      const nextFields = prev.fields.map((field, index) => {
        if (index !== fieldIndex) return field;
        const options = normalizeFloorLayoutOptions(field.options).filter((option) => option.id !== optionId);
        return { ...field, options };
      });
      return { ...prev, fields: nextFields };
    });
  };

  const uploadFloorLayoutImage = async (fieldIndex: number, file: File) => {
    if (!file.type.startsWith('image/')) {
      setFeedback('Please choose an image file for floor layout options.');
      return;
    }

    setFloorLayoutUploadByIndex((prev) => ({ ...prev, [fieldIndex]: true }));
    try {
      const extension = file.name.includes('.') ? file.name.split('.').pop() || 'png' : 'png';
      const baseName = slugifyFieldKey(file.name.replace(/\.[^/.]+$/, '')) || 'layout';
      const filePath = `facility-layouts/${Date.now()}_${baseName}.${extension}`;
      const { error } = await supabase.storage
        .from(FLOOR_LAYOUT_IMAGE_BUCKET)
        .upload(filePath, file, { upsert: false, contentType: file.type || 'image/png' });
      if (error) throw error;

      const { data } = supabase.storage.from(FLOOR_LAYOUT_IMAGE_BUCKET).getPublicUrl(filePath);
      const imageSrc = data.publicUrl;
      if (!imageSrc) throw new Error('Could not resolve image URL after upload.');

      setFormDraft((prev) => {
        const nextFields = prev.fields.map((field, index) => {
          if (index !== fieldIndex) return field;
          const existing = normalizeFloorLayoutOptions(field.options);
          const nextOption: FloorLayoutOption = {
            id: `${baseName}_${Date.now()}`,
            label: file.name.replace(/\.[^/.]+$/, '') || `Layout ${existing.length + 1}`,
            imageSrc,
          };
          return { ...field, options: [...existing, nextOption] };
        });
        return { ...prev, fields: nextFields };
      });
      setFeedback('Floor layout image uploaded.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Could not upload floor layout image.');
    } finally {
      setFloorLayoutUploadByIndex((prev) => ({ ...prev, [fieldIndex]: false }));
    }
  };

  const addFormField = () => {
    const customFieldCount = formDraft.fields.filter((field) => !isCoreFormField(field)).length;
    const nextCustomNumber = customFieldCount + 1;
    const fieldKey = `custom_field_${nextCustomNumber}`;
    setFormDraft((prev) => ({
      ...prev,
      fields: [
        ...prev.fields,
        {
          field_key: fieldKey,
          label: `Custom field ${nextCustomNumber}`,
          field_type: 'short_text',
          is_required: false,
          sort_order: prev.fields.length,
          options: [],
          validation: {
            is_core: false,
          },
          visibility_rules: {},
          placeholder: null,
          help_text: null,
        },
      ],
    }));
  };

  const removeFormField = (index: number) => {
    setFormDraft((prev) => ({
      ...prev,
      fields: prev.fields
        .filter((field, fieldIndex) => fieldIndex !== index || isCoreFormField(field))
        .map((field, fieldIndex) => ({ ...field, sort_order: fieldIndex })),
    }));
  };

  const moveFormField = (index: number, direction: -1 | 1) => {
    setFormDraft((prev) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= prev.fields.length) return prev;
      if (isCoreFormField(prev.fields[index]) !== isCoreFormField(prev.fields[targetIndex])) return prev;
      const nextFields = [...prev.fields];
      const [item] = nextFields.splice(index, 1);
      nextFields.splice(targetIndex, 0, item);
      return {
        ...prev,
        fields: nextFields.map((field, fieldIndex) => ({
          ...field,
          sort_order: fieldIndex,
        })),
      };
    });
  };

  const saveFormBuilder = async () => {
    const normalizedFields = formDraft.fields.map((field, index) => {
      const normalizedLabel = field.label.trim();
      const validation = readValidationRecord(field.validation);
      const coreKey = String(validation.core_key || '').trim();
      const isCore = Boolean(validation.is_core) && Boolean(coreKey);
      const fallbackKey = slugifyFieldKey(normalizedLabel || `custom_field_${index + 1}`);
      const normalizedKey = isCore
        ? coreFieldStorageKey(coreKey)
        : slugifyFieldKey(field.field_key || fallbackKey) || fallbackKey;
      return {
        ...field,
        field_key: normalizedKey,
        label: normalizedLabel || `Custom field ${index + 1}`,
        sort_order: index,
        validation: {
          ...validation,
          is_core: isCore,
          core_key: isCore ? coreKey : null,
        },
      };
    });

    const uniqueKeys = new Set<string>();
    for (const field of normalizedFields) {
      if (uniqueKeys.has(field.field_key)) {
        setFeedback(`Field key "${field.field_key}" is duplicated. Please make each field key unique.`);
        return;
      }
      uniqueKeys.add(field.field_key);
    }

    try {
      await saveFacilityRequestFormMutation.mutateAsync({
        ...formDraft,
        form_key: 'facility_booking',
        fields: normalizedFields,
      });
      setFeedback('Facility custom questions saved.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Could not save facility custom questions.');
    }
  };

  const toggleBlackoutLocation = (location: string) => {
    setBlackoutDraft((prev) => {
      const selected = prev.location_of_event.includes(location);
      const nextLocations = selected
        ? prev.location_of_event.filter((entry) => entry !== location)
        : [...prev.location_of_event, location];

      return {
        ...prev,
        location_of_event: nextLocations,
        location_other: location === 'Other' && selected ? '' : prev.location_other,
      };
    });
  };

  const addBlackout = async () => {
    const startsAt = fromLocalInputValue(blackoutDraft.starts_at);
    const endsAt = fromLocalInputValue(blackoutDraft.ends_at);
    if (!startsAt || !endsAt) {
      setFeedback('Start and end are required for blackout dates.');
      return;
    }
    if (new Date(endsAt) <= new Date(startsAt)) {
      setFeedback('Blackout end time must be after the start time.');
      return;
    }
    if (blackoutDraft.location_of_event.includes('Other') && !blackoutDraft.location_other.trim()) {
      setFeedback('Please describe the blackout location when "Other" is selected.');
      return;
    }
    try {
      await createBlackoutMutation.mutateAsync({
        title: blackoutDraft.title || 'Facility Blackout',
        starts_at: startsAt,
        ends_at: endsAt,
        reason: blackoutDraft.reason || null,
        location_of_event: blackoutDraft.location_of_event,
        location_other: blackoutDraft.location_of_event.includes('Other')
          ? blackoutDraft.location_other.trim() || null
          : null,
        all_day: blackoutDraft.all_day,
      });
      setBlackoutDraft({
        title: 'Facility Blackout',
        starts_at: '',
        ends_at: '',
        reason: '',
        location_of_event: [],
        location_other: '',
        all_day: false,
      });
      setFeedback('Blackout date added.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Could not add blackout date.');
    }
  };

  const runSync = async () => {
    try {
      const result = await syncCalendarMutation.mutateAsync();
      setFeedback(
        `Calendar synced. Pushed ${result.data.pushedToGoogle}, pulled ${result.data.pulledFromGoogle}, removed ${result.data.removedCancelled}.`
      );
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Could not sync calendars.');
    }
  };

  const deleteRequest = async (request: FacilityRequest) => {
    const confirmed = window.confirm(`Delete request "${request.event_name || 'Facility booking request'}"?`);
    if (!confirmed) return;
    try {
      await deleteRequestMutation.mutateAsync(request.id);
      setFeedback('Request deleted.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Could not delete request.');
    }
  };

  const calendarDays = React.useMemo(() => {
    const first = startOfMonth(calendarMonth);
    const leading = first.getDay();
    const monthLength = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const totalCells = Math.ceil((leading + monthLength) / 7) * 7;

    return Array.from({ length: totalCells }, (_, index) => {
      const dateNumber = index - leading + 1;
      if (dateNumber < 1 || dateNumber > monthLength) return null;
      return new Date(first.getFullYear(), first.getMonth(), dateNumber);
    });
  }, [calendarMonth]);

  const entriesByDay = React.useMemo(() => {
    const map = new Map<string, CalendarEntry[]>();
    const pushEntry = (dayKey: string, entry: CalendarEntry) => {
      const current = map.get(dayKey) || [];
      current.push(entry);
      map.set(dayKey, current);
    };

    blackoutDates.forEach((blackout) => {
      const startsAt = new Date(blackout.starts_at);
      const endsAt = new Date(blackout.ends_at);
      if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime()) || endsAt <= startsAt) return;
      const blackoutScope = formatBlackoutScope(blackout.location_of_event, blackout.location_other);
      eachDayInRange(startsAt, endsAt).forEach((day) =>
        pushEntry(formatDayKey(day), {
          id: `blackout-${blackout.id}`,
          tone: 'blackout',
          title: blackout.title || 'Facility Blackout',
          subtitle: `${blackoutScope} • ${formatDisplayDateTime(blackout.starts_at)} – ${formatDisplayDateTime(blackout.ends_at)}`,
          locationSummary: blackoutScope,
        })
      );
    });

    requests
      .filter((request) => request.status === 'approved' || request.status === 'pending')
      .forEach((request) => {
        const startsAt = new Date(request.requested_start_at);
        const endsAt = new Date(request.requested_end_at);
        if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime()) || endsAt <= startsAt) return;
        const tone: CalendarEntry['tone'] = request.status === 'approved' ? 'booking' : 'pending';
        const locationSummary = formatLocationSummary(request.location_of_event, request.location_other);
        eachDayInRange(startsAt, endsAt).forEach((day) =>
          pushEntry(formatDayKey(day), {
            id: `${request.status}-${request.id}`,
            tone,
            title: request.event_name || 'Facility Request',
            subtitle: `${locationSummary} • ${request.requester_name} • ${formatDisplayDateTime(request.requested_start_at)} – ${formatDisplayDateTime(request.requested_end_at)}`,
            locationSummary,
          })
        );
      });

    return map;
  }, [blackoutDates, requests]);

  const selectedDayEntries = entriesByDay.get(selectedDayKey) || [];
  const selectedDayDate = new Date(`${selectedDayKey}T12:00:00`);

  const isAdminNotesOpen = (id: string) => Boolean(adminNotesOpen[id]);
  const toggleAdminNotes = (id: string) => setAdminNotesOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  const isRequestExpanded = (request: FacilityRequest) =>
    requestExpanded[request.id] ?? (request.status === 'pending' || request.status === 'needs_info');
  const toggleRequestExpanded = (id: string) => setRequestExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <PageOuter>
      <Page>
      <HeroCard>
        <HeroHeader>
          <HeroHeading>
            <Title>Facilities Admin</Title>
            <Muted>
              Review requests, manage calendar and blackout dates, and configure dynamic booking questions from one place.
            </Muted>
          </HeroHeading>
          <MenuWrap ref={settingsMenuRef}>
            <MenuButton
              type="button"
              aria-haspopup="menu"
              aria-expanded={settingsMenuOpen}
              onClick={() => setSettingsMenuOpen((open) => !open)}
            >
              Settings menu
            </MenuButton>
            {settingsMenuOpen ? (
              <MenuPanel role="menu" aria-label="Facility settings">
                <SubTitle>Settings</SubTitle>
                <Grid>
                  <Field>
                    Facilities Group
                    <Select
                      value={settingsDraft.facilities_group_id}
                      onChange={(event) =>
                        setSettingsDraft((prev) => ({ ...prev, facilities_group_id: event.target.value }))
                      }
                      disabled={groupsLoading || updateSettingsMutation.isPending}
                    >
                      <option value="">None</option>
                      {groupOptions.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.name}
                          {group.category ? ` (${group.category})` : ''}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field>
                    Primary Google Calendar ID
                    <Input
                      value={settingsDraft.google_calendar_id}
                      onChange={(event) => setSettingsDraft((prev) => ({ ...prev, google_calendar_id: event.target.value }))}
                      placeholder="example@group.calendar.google.com"
                    />
                  </Field>
                  <Field>
                    Facilities Group Calendar ID
                    <Input
                      value={settingsDraft.facilities_group_calendar_id}
                      onChange={(event) =>
                        setSettingsDraft((prev) => ({ ...prev, facilities_group_calendar_id: event.target.value }))
                      }
                      placeholder="Optional second calendar id"
                    />
                  </Field>
                </Grid>
                <Row>
                  <Button type="button" onClick={saveSettings} disabled={updateSettingsMutation.isPending}>
                    {updateSettingsMutation.isPending ? 'Saving...' : 'Save settings'}
                  </Button>
                  <SecondaryButton type="button" onClick={runSync} disabled={syncCalendarMutation.isPending}>
                    {syncCalendarMutation.isPending ? 'Syncing...' : 'Sync Google Calendar'}
                  </SecondaryButton>
                </Row>
              </MenuPanel>
            ) : null}
          </MenuWrap>
        </HeroHeader>
        {feedback ? <FeedbackBanner role="status">{feedback}</FeedbackBanner> : null}
      </HeroCard>

      <Card>
        <SubTitle>Workspace</SubTitle>
        <TabBar role="tablist" aria-label="Facility admin sections">
          <TabButton
            type="button"
            role="tab"
            $active={activeTab === 'requests'}
            aria-selected={activeTab === 'requests'}
            onClick={() => setActiveTab('requests')}
          >
            Requests
          </TabButton>
          <TabButton
            type="button"
            role="tab"
            $active={activeTab === 'calendar'}
            aria-selected={activeTab === 'calendar'}
            onClick={() => setActiveTab('calendar')}
          >
            Calendar & blackouts
          </TabButton>
          <TabButton
            type="button"
            role="tab"
            $active={activeTab === 'form_builder'}
            aria-selected={activeTab === 'form_builder'}
            onClick={() => setActiveTab('form_builder')}
          >
            Form builder
          </TabButton>
        </TabBar>
      </Card>

      {activeTab === 'calendar' ? (
      <Card>
        <SubTitle>Facilities Calendar</SubTitle>
        <CalendarShell>
          <CalendarHeader>
            <Row>
              <CalendarNavButton
                type="button"
                onClick={() => setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}
              >
                Previous
              </CalendarNavButton>
              <CalendarNavButton
                type="button"
                onClick={() => setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}
              >
                Next
              </CalendarNavButton>
            </Row>
            <CalendarMonthLabel>
              {calendarMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </CalendarMonthLabel>
          </CalendarHeader>

          <CalendarLegend>
            <LegendItem>
              <LegendDot $color="rgb(22, 163, 74)" />
              Approved room booking
            </LegendItem>
            <LegendItem>
              <LegendDot $color="rgb(239, 68, 68)" />
              Blackout date
            </LegendItem>
            <LegendItem>
              <LegendDot $color="rgb(245, 158, 11)" />
              Pending request
            </LegendItem>
          </CalendarLegend>

          <CalendarWeekdays>
            {CALENDAR_WEEKDAYS.map((weekday) => (
              <CalendarWeekday key={weekday}>{weekday}</CalendarWeekday>
            ))}
          </CalendarWeekdays>

          <CalendarGrid>
            {calendarDays.map((day, index) => {
              if (!day) {
                return <div key={`blank-${index}`} />;
              }

              const dayKey = formatDayKey(day);
              const entries = entriesByDay.get(dayKey) || [];
              const blackoutCount = entries.filter((entry) => entry.tone === 'blackout').length;
              const bookingCount = entries.filter((entry) => entry.tone === 'booking').length;
              const pendingCount = entries.filter((entry) => entry.tone === 'pending').length;
              const hasBlackout = blackoutCount > 0;
              const hasBooked = bookingCount > 0;
              const hasPending = entries.some((entry) => entry.tone === 'pending');
              const isSelected = selectedDayKey === dayKey;

              return (
                <CalendarDayCell
                  key={dayKey}
                  type="button"
                  $selected={isSelected}
                  $hasBlackout={hasBlackout}
                  $hasBooked={hasBooked}
                  $hasPending={hasPending}
                  onClick={() => setSelectedDayKey(dayKey)}
                >
                  <CalendarDayNumber>{day.getDate()}</CalendarDayNumber>
                  <DaySummary>
                    {bookingCount > 0 ? (
                      <DayPill $tone="booking">{formatCountLabel(bookingCount, 'booking', 'bookings')}</DayPill>
                    ) : null}
                    {blackoutCount > 0 ? <DayPill $tone="blackout">Blocked</DayPill> : null}
                    {pendingCount > 0 ? (
                      <DayPill $tone="pending">{formatCountLabel(pendingCount, 'pending', 'pending')}</DayPill>
                    ) : null}
                  </DaySummary>
                </CalendarDayCell>
              );
            })}
          </CalendarGrid>

          <SelectedDayPanel>
            <strong>
              {Number.isNaN(selectedDayDate.getTime())
                ? 'Selected day'
                : selectedDayDate.toLocaleDateString(undefined, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
            </strong>
            {selectedDayEntries.length === 0 ? (
              <Muted>No room bookings, blackout dates, or pending requests for this day.</Muted>
            ) : (
              selectedDayEntries.map((entry) => (
                <SelectedDayItem key={entry.id}>
                  <Row>
                    <DayPill $tone={entry.tone}>
                      {entry.tone === 'booking' ? 'Approved' : entry.tone === 'blackout' ? 'Blocked' : 'Pending'}
                    </DayPill>
                    <strong>{entry.title}</strong>
                  </Row>
                  <Muted>{entry.subtitle}</Muted>
                </SelectedDayItem>
              ))
            )}
          </SelectedDayPanel>
        </CalendarShell>
      </Card>
      ) : null}

      {activeTab === 'calendar' ? (
      <Card>
        <SubTitle>Blackout Dates</SubTitle>
        <Grid>
          <Field>
            Title
            <Input
              value={blackoutDraft.title}
              onChange={(event) => setBlackoutDraft((prev) => ({ ...prev, title: event.target.value }))}
            />
          </Field>
          <Field>
            Start
            <Input
              type="datetime-local"
              value={blackoutDraft.starts_at}
              onChange={(event) => setBlackoutDraft((prev) => ({ ...prev, starts_at: event.target.value }))}
            />
          </Field>
          <Field>
            End
            <Input
              type="datetime-local"
              value={blackoutDraft.ends_at}
              onChange={(event) => setBlackoutDraft((prev) => ({ ...prev, ends_at: event.target.value }))}
            />
          </Field>
          <Field>
            Reason
            <Input
              value={blackoutDraft.reason}
              onChange={(event) => setBlackoutDraft((prev) => ({ ...prev, reason: event.target.value }))}
              placeholder="Optional reason"
            />
          </Field>
        </Grid>
        <Field style={{ marginTop: '0.75rem' }}>
          Location scope
          <Muted>Leave all locations unchecked to make this blackout campus-wide.</Muted>
          <ChoiceGrid>
            {FACILITY_LOCATION_OPTIONS.map((location) => (
              <ChoiceOption key={location}>
                <input
                  type="checkbox"
                  checked={blackoutDraft.location_of_event.includes(location)}
                  onChange={() => toggleBlackoutLocation(location)}
                />
                <span>{location}</span>
              </ChoiceOption>
            ))}
          </ChoiceGrid>
        </Field>
        {blackoutDraft.location_of_event.includes('Other') ? (
          <Field style={{ marginTop: '0.75rem' }}>
            Other blackout location
            <Input
              value={blackoutDraft.location_other}
              onChange={(event) => setBlackoutDraft((prev) => ({ ...prev, location_other: event.target.value }))}
              placeholder="Describe the room or area"
            />
          </Field>
        ) : null}
        <Row>
          <label>
            <input
              type="checkbox"
              checked={blackoutDraft.all_day}
              onChange={(event) => setBlackoutDraft((prev) => ({ ...prev, all_day: event.target.checked }))}
            />{' '}
            All day
          </label>
          <Button type="button" onClick={addBlackout} disabled={createBlackoutMutation.isPending}>
            {createBlackoutMutation.isPending ? 'Adding...' : 'Add blackout date'}
          </Button>
        </Row>
        <BlackoutList>
          {blackoutDates.map((date) => (
            <BlackoutItem key={date.id}>
              <div>
                <strong>{date.title}</strong>
                <Muted>
                  {formatDisplayDateTime(date.starts_at)} – {formatDisplayDateTime(date.ends_at)} ({date.source})
                </Muted>
                <Muted>{formatBlackoutScope(date.location_of_event, date.location_other)}</Muted>
              </div>
              <SecondaryButton
                type="button"
                onClick={() => deleteBlackoutMutation.mutate(date.id)}
                disabled={deleteBlackoutMutation.isPending}
              >
                Remove
              </SecondaryButton>
            </BlackoutItem>
          ))}
          {blackoutDates.length === 0 ? <Muted>No blackout dates yet.</Muted> : null}
        </BlackoutList>
      </Card>
      ) : null}

      {activeTab === 'requests' ? (
      <Card>
        <SubTitle>Facility Requests</SubTitle>
        {loadingRequests ? <Muted>Loading requests...</Muted> : null}
        <RequestList>
          {requests.map((request) => {
            const draft = getDraftForRequest(request);
            const expanded = isRequestExpanded(request);
            const notesExpanded = isAdminNotesOpen(request.id);
            const notesPreview =
              draft.adminNotes.trim() && !notesExpanded ? truncateOneLine(draft.adminNotes) : null;
            return (
              <RequestCard
                key={request.id}
                id={`facility-request-${request.id}`}
                $status={draft.status}
                $highlighted={focusedRequestId === request.id}
              >
                <RequestCardHeader>
                  <RequestCardHeaderRow>
                    <CollapseButton
                      type="button"
                      onClick={() => toggleRequestExpanded(request.id)}
                      aria-expanded={expanded}
                      aria-controls={`facility-request-body-${request.id}`}
                    >
                      {expanded ? 'Collapse details' : 'Expand details'}
                    </CollapseButton>
                  </RequestCardHeaderRow>
                  <RequestCardTop>
                    <RequestHeading>
                      <RequestTitleRow>
                        <RequestTitle>{request.event_name || 'Facility booking request'}</RequestTitle>
                        <SourceChip>{request.source}</SourceChip>
                      </RequestTitleRow>
                      <RequestMeta>
                        <RequestMetaPrimary>
                          {request.requester_name} · {request.requester_email}
                        </RequestMetaPrimary>
                        <RequestMetaSecondary>
                          {formatDisplayDateTime(request.requested_start_at)} →{' '}
                          {formatDisplayDateTime(request.requested_end_at)}
                        </RequestMetaSecondary>
                      </RequestMeta>
                    </RequestHeading>
                    <StatusPill $status={draft.status}>{STATUS_BADGE_TEXT[draft.status]}</StatusPill>
                  </RequestCardTop>
                </RequestCardHeader>
                {expanded ? (
                  <div id={`facility-request-body-${request.id}`}>
                    <RequestDetailsPanel>
                      <RequestDetails>
                        <RequestDetail>
                          <dt>Contact phone</dt>
                          <dd>{formatOptional(request.requester_phone)}</dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Organization</dt>
                          <dd>{formatOptional(request.organization_name)}</dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Event name</dt>
                          <dd>{formatOptional(request.event_name)}</dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Ministry / Group</dt>
                          <dd>
                            {formatOptional(request.ministry_group)}
                            {request.ministry_group === 'Other' && request.ministry_group_other
                              ? ` (${request.ministry_group_other})`
                              : ''}
                          </dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Event type</dt>
                          <dd>
                            {formatOptional(request.event_type)}
                            {request.event_type === 'Other' && request.event_type_other
                              ? ` (${request.event_type_other})`
                              : ''}
                          </dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Attendees</dt>
                          <dd>{formatOptional(request.attendee_count)}</dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Requested dates</dt>
                          <dd>
                            {formatDisplayDateTime(request.requested_start_at)} – {formatDisplayDateTime(request.requested_end_at)}
                          </dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Event hours</dt>
                          <dd>
                            {formatOptional(request.event_start_time)} - {formatOptional(request.event_end_time)}
                          </dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Locations</dt>
                          <dd>
                            {formatList(request.location_of_event)}
                            {request.location_of_event?.includes('Other') && request.location_other
                              ? ` (${request.location_other})`
                              : ''}
                          </dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Table types</dt>
                          <dd>{formatList(request.table_types)}</dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Tables needed</dt>
                          <dd>{formatOptional(request.number_of_tables_needed)}</dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Food tables</dt>
                          <dd>{formatOptional(request.food_table_count ?? 0)}</dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Table linen</dt>
                          <dd>{formatYesNo(request.needs_table_linen)}</dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Floor layout</dt>
                          <dd>{formatOptional(request.floor_layout)}</dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Recurring</dt>
                          <dd>
                            {request.is_recurring_event
                              ? `${formatOptional(request.recurrence_frequency)}${
                                  request.recurrence_frequency === 'Other' && request.recurrence_frequency_other
                                    ? ` (${request.recurrence_frequency_other})`
                                    : ''
                                }`
                              : 'No'}
                          </dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Recurs until</dt>
                          <dd>
                            {request.is_recurring_event && request.recurrence_ends_at
                              ? formatDisplayDateTime(request.recurrence_ends_at)
                              : '—'}
                          </dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>A/V needed</dt>
                          <dd>{formatYesNo(request.has_audio_visual_needs)}</dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>A/V details</dt>
                          <dd>{formatOptional(request.audio_visual_needs_details)}</dd>
                        </RequestDetail>
                        <RequestDetail>
                          <dt>Description</dt>
                          <dd>{formatOptional(request.description_of_event || request.message)}</dd>
                        </RequestDetail>
                        {Array.isArray(request.facility_request_form_responses) &&
                        request.facility_request_form_responses.length > 0
                          ? request.facility_request_form_responses.map((response) => (
                              <RequestDetail key={response.id}>
                                <dt>{response.field_label || response.field_key}</dt>
                                <dd>{formatCustomAnswer(response.answer_value)}</dd>
                              </RequestDetail>
                            ))
                          : null}
                      </RequestDetails>
                    </RequestDetailsPanel>
                    <RequestCardFooter>
                      <RequestStatusField>
                        Status
                        <Select
                          value={draft.status}
                          onChange={(event) =>
                            onRequestDraftChange(request, { status: event.target.value as FacilityRequestStatus })
                          }
                        >
                          {STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>
                              {STATUS_DISPLAY[status]}
                            </option>
                          ))}
                        </Select>
                      </RequestStatusField>
                      <Field>
                        Response to requester
                        <TextArea
                          value={draft.adminResponse}
                          onChange={(event) => onRequestDraftChange(request, { adminResponse: event.target.value })}
                          placeholder="Message sent to requester with approval/decline details"
                        />
                      </Field>
                      <AdminNotesCollapsible>
                        <NotesToggle
                          type="button"
                          onClick={() => toggleAdminNotes(request.id)}
                          aria-expanded={notesExpanded}
                          id={`admin-notes-toggle-${request.id}`}
                        >
                          <NotesToggleLeft>
                            <span>Admin notes</span>
                            {notesPreview ? <NotesPreview>{notesPreview}</NotesPreview> : null}
                          </NotesToggleLeft>
                          <NotesChevron $open={notesExpanded} aria-hidden>
                            ›
                          </NotesChevron>
                        </NotesToggle>
                        {notesExpanded ? (
                          <TextArea
                            value={draft.adminNotes}
                            onChange={(event) => onRequestDraftChange(request, { adminNotes: event.target.value })}
                            aria-labelledby={`admin-notes-toggle-${request.id}`}
                            placeholder="Internal notes (not shown to the requester)"
                          />
                        ) : null}
                      </AdminNotesCollapsible>
                      <RequestActionsRow>
                        <Button
                          type="button"
                          onClick={() => saveRequest(request)}
                          disabled={updateRequestMutation.isPending}
                        >
                          {updateRequestMutation.isPending ? 'Saving...' : 'Save request'}
                        </Button>
                        <DangerButton
                          type="button"
                          onClick={() => deleteRequest(request)}
                          disabled={deleteRequestMutation.isPending}
                        >
                          {deleteRequestMutation.isPending ? 'Deleting...' : 'Delete request'}
                        </DangerButton>
                      </RequestActionsRow>
                    </RequestCardFooter>
                  </div>
                ) : null}
              </RequestCard>
            );
          })}
          {requests.length === 0 && !loadingRequests ? <Muted>No facility requests yet.</Muted> : null}
        </RequestList>
      </Card>
      ) : null}

      {activeTab === 'form_builder' ? (
      <Card>
        <SubTitle>Facility Form Builder</SubTitle>
        <Muted>
          Core booking fields and optional dynamic questions for website/mobile are shown below.
        </Muted>
        <RequestDetailsPanel style={{ marginTop: '0.75rem' }}>
          <SubTitle>Core Booking Fields</SubTitle>
          <Muted>Edit labels, required flags, and options for the existing booking questions.</Muted>
          <RequestList style={{ marginTop: '0.6rem' }}>
            {coreFieldEntries.map(({ field, index }) => {
              const optionsText = optionsToMultilineText(field.options);
              const validation = readValidationRecord(field.validation);
              const isFloorLayoutField = String(validation.core_key || '').trim() === FLOOR_LAYOUT_CORE_KEY;
              const floorLayoutOptions = isFloorLayoutField ? normalizeFloorLayoutOptions(field.options) : [];
              return (
                <RequestDetailsPanel key={`${field.field_key}-${index}`}>
                  <Grid>
                    <Field>
                      Label
                      <Input value={field.label} onChange={(event) => updateFormField(index, { label: event.target.value })} />
                    </Field>
                    <Field>
                      Field key
                      <Input value={field.field_key} disabled />
                    </Field>
                    <Field>
                      Field type
                      <Input value={fieldTypeLabel(field.field_type)} disabled />
                    </Field>
                    <Field>
                      Placeholder
                      <Input
                        value={field.placeholder || ''}
                        onChange={(event) => updateFormField(index, { placeholder: event.target.value || null })}
                        placeholder="Optional placeholder text"
                      />
                    </Field>
                  </Grid>
                  {FORM_FIELD_TYPES_REQUIRING_OPTIONS.includes(field.field_type) && !isFloorLayoutField ? (
                    <Field style={{ marginTop: '0.6rem' }}>
                      Options (one per line)
                      <TextArea
                        value={optionsText}
                        onChange={(event) =>
                          updateFormField(index, {
                            options: parseOptionsText(event.target.value),
                          })
                        }
                        placeholder="Option A&#10;Option B&#10;Option C"
                      />
                    </Field>
                  ) : null}
                  {isFloorLayoutField ? (
                    <div style={{ marginTop: '0.6rem', display: 'grid', gap: '0.6rem' }}>
                      <Muted>Upload one or more floor layout images for members to select from.</Muted>
                      <Row>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) void uploadFloorLayoutImage(index, file);
                            event.currentTarget.value = '';
                          }}
                          disabled={floorLayoutUploadByIndex[index]}
                        />
                        {floorLayoutUploadByIndex[index] ? <Muted>Uploading image...</Muted> : null}
                      </Row>
                      {floorLayoutOptions.length === 0 ? <Muted>No layout images uploaded yet.</Muted> : null}
                      {floorLayoutOptions.map((option) => (
                        <RequestDetail key={option.id}>
                          <img
                            src={option.imageSrc}
                            alt={option.label}
                            style={{
                              width: '100%',
                              maxWidth: '180px',
                              borderRadius: '10px',
                              border: '1px solid color-mix(in srgb, var(--outline) 60%, transparent)',
                              objectFit: 'cover',
                            }}
                          />
                          <Field>
                            Option label
                            <Input
                              value={option.label}
                              onChange={(event) =>
                                updateFloorLayoutOption(index, option.id, {
                                  label: event.target.value || option.label,
                                  id: slugifyFieldKey(event.target.value || option.label) || option.id,
                                })
                              }
                            />
                          </Field>
                          <Field>
                            Image URL
                            <Input
                              value={option.imageSrc}
                              onChange={(event) =>
                                updateFloorLayoutOption(index, option.id, {
                                  imageSrc: event.target.value,
                                })
                              }
                            />
                          </Field>
                          <DangerButton type="button" onClick={() => removeFloorLayoutOption(index, option.id)}>
                            Remove image
                          </DangerButton>
                        </RequestDetail>
                      ))}
                    </div>
                  ) : null}
                  <Field style={{ marginTop: '0.6rem' }}>
                    Help text
                    <Input
                      value={field.help_text || ''}
                      onChange={(event) => updateFormField(index, { help_text: event.target.value || null })}
                      placeholder="Optional helper text under the question"
                    />
                  </Field>
                  <Row style={{ marginTop: '0.6rem' }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={field.is_required}
                        onChange={(event) => updateFormField(index, { is_required: event.target.checked })}
                      />{' '}
                      Required
                    </label>
                  </Row>
                </RequestDetailsPanel>
              );
            })}
          </RequestList>
        </RequestDetailsPanel>
        <Grid style={{ marginTop: '0.75rem' }}>
          <Field>
            Form title
            <Input
              value={formDraft.title}
              onChange={(event) => setFormDraft((prev) => ({ ...prev, title: event.target.value }))}
              placeholder="Facility Booking Additional Questions"
            />
          </Field>
          <Field>
            Description
            <Input
              value={formDraft.description || ''}
              onChange={(event) => setFormDraft((prev) => ({ ...prev, description: event.target.value }))}
              placeholder="Optional helper text shown above custom questions"
            />
          </Field>
        </Grid>
        <Row style={{ marginTop: '0.6rem', marginBottom: '0.65rem' }}>
          <label>
            <input
              type="checkbox"
              checked={formDraft.is_enabled}
              onChange={(event) => setFormDraft((prev) => ({ ...prev, is_enabled: event.target.checked }))}
            />{' '}
            Enable custom booking questions
          </label>
          <Button type="button" onClick={addFormField}>
            Add field
          </Button>
        </Row>
        <SubTitle>Additional Custom Questions</SubTitle>
        <RequestList>
          {customFieldEntries.map(({ field, index }) => {
            const optionsText = optionsToMultilineText(field.options);
            return (
              <RequestDetailsPanel key={`${field.field_key}-${index}`}>
                <Grid>
                  <Field>
                    Label
                    <Input
                      value={field.label}
                      onChange={(event) => {
                        const nextLabel = event.target.value;
                        const shouldAutoUpdateKey = !field.field_key || /^custom_field_\d+$/.test(field.field_key);
                        updateFormField(index, {
                          label: nextLabel,
                          field_key: shouldAutoUpdateKey ? slugifyFieldKey(nextLabel) : field.field_key,
                        });
                      }}
                    />
                  </Field>
                  <Field>
                    Field key
                    <Input
                      value={field.field_key}
                      onChange={(event) => updateFormField(index, { field_key: slugifyFieldKey(event.target.value) })}
                      placeholder="example: seating_preference"
                    />
                  </Field>
                  <Field>
                    Field type
                    <Select
                      value={field.field_type}
                      onChange={(event) =>
                        updateFormField(index, {
                          field_type: event.target.value as FacilityRequestFormFieldType,
                        })
                      }
                    >
                      {FORM_FIELD_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field>
                    Placeholder
                    <Input
                      value={field.placeholder || ''}
                      onChange={(event) => updateFormField(index, { placeholder: event.target.value || null })}
                      placeholder="Optional placeholder text"
                    />
                  </Field>
                </Grid>
                {FORM_FIELD_TYPES_REQUIRING_OPTIONS.includes(field.field_type) ? (
                  <Field style={{ marginTop: '0.6rem' }}>
                    Options (one per line)
                    <TextArea
                      value={optionsText}
                      onChange={(event) =>
                        updateFormField(index, {
                          options: parseOptionsText(event.target.value),
                        })
                      }
                      placeholder="Option A&#10;Option B&#10;Option C"
                    />
                  </Field>
                ) : null}
                <Field style={{ marginTop: '0.6rem' }}>
                  Help text
                  <Input
                    value={field.help_text || ''}
                    onChange={(event) => updateFormField(index, { help_text: event.target.value || null })}
                    placeholder="Optional helper text under the question"
                  />
                </Field>
                <Row style={{ marginTop: '0.6rem' }}>
                  <label>
                    <input
                      type="checkbox"
                      checked={field.is_required}
                      onChange={(event) => updateFormField(index, { is_required: event.target.checked })}
                    />{' '}
                    Required
                  </label>
                  <SecondaryButton
                    type="button"
                    onClick={() => moveFormField(index, -1)}
                    disabled={index === 0 || isCoreFormField(formDraft.fields[index - 1])}
                  >
                    Move up
                  </SecondaryButton>
                  <SecondaryButton
                    type="button"
                    onClick={() => moveFormField(index, 1)}
                    disabled={index === formDraft.fields.length - 1 || isCoreFormField(formDraft.fields[index + 1])}
                  >
                    Move down
                  </SecondaryButton>
                  <DangerButton type="button" onClick={() => removeFormField(index)}>
                    Remove
                  </DangerButton>
                </Row>
              </RequestDetailsPanel>
            );
          })}
          {customFieldEntries.length === 0 ? (
            <Muted>No custom fields yet. Add a field to start building your dynamic questions.</Muted>
          ) : null}
        </RequestList>
        <Row style={{ marginTop: '0.8rem' }}>
          <Button type="button" onClick={saveFormBuilder} disabled={saveFacilityRequestFormMutation.isPending}>
            {saveFacilityRequestFormMutation.isPending ? 'Saving...' : 'Save custom questions'}
          </Button>
        </Row>
      </Card>
      ) : null}
      </Page>
    </PageOuter>
  );
};

export default FacilityAdminPage;
